import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ShieldCheck, ArrowLeft, Loader2, CheckCircle2, Eye, EyeOff } from "lucide-react";
import { formatApiErrorDetail } from "@/context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password !== confirm) {
      setError("Passwords don't match.");
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API}/auth/reset-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, password }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(formatApiErrorDetail(data.detail));
      setDone(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const inputCls = "h-12 w-full rounded-md border border-white/10 bg-[#07090e] px-4 text-sm text-slate-50 placeholder:text-slate-600 focus:border-amber-500/60 focus:outline-none";

  return (
    <div data-testid="reset-password-page" className="flex min-h-screen flex-col bg-[#07090e]">
      <header className="border-b border-white/5 bg-[#07090e]/80 backdrop-blur-xl">
        <div className="mx-auto flex max-w-4xl items-center justify-between px-4 py-4 sm:px-6">
          <Link to="/" data-testid="reset-back" className="flex items-center gap-2 text-slate-400 transition-colors hover:text-amber-500">
            <ArrowLeft className="h-4 w-4" />
            <span className="font-mono-tag text-[10px]">Back</span>
          </Link>
          <span className="font-display text-lg font-extrabold uppercase text-slate-50">Guard<span className="text-amber-500">Gig</span>28</span>
          <span className="font-mono-tag text-[9px] text-slate-600">guardgig28.com</span>
        </div>
      </header>

      <main className="flex flex-1 items-center justify-center px-4 py-16">
        <div className="w-full max-w-md rounded-2xl border border-amber-500/25 bg-[#0f141c] p-8">
          <div className="flex items-center gap-2.5">
            <span className="flex h-9 w-9 items-center justify-center rounded-md bg-amber-500 text-[#07090e]">
              <ShieldCheck className="h-5 w-5" strokeWidth={2.5} />
            </span>
            <h1 className="font-display text-xl font-extrabold uppercase text-slate-50">Choose a new password</h1>
          </div>

          {!token ? (
            <p data-testid="reset-no-token" className="mt-6 text-sm leading-relaxed text-slate-400">
              This reset link is missing its token. Use the full link from your email, or request a fresh one from the sign-in screen.
            </p>
          ) : done ? (
            <div data-testid="reset-success" className="mt-6 text-center">
              <CheckCircle2 className="mx-auto h-12 w-12 text-emerald-400" />
              <p className="font-display mt-4 text-lg font-bold uppercase text-slate-50">Password updated</p>
              <p className="mt-2 text-sm text-slate-400">Sign in with your new password — you're back in.</p>
              <Link
                data-testid="reset-done-home"
                to="/"
                className="mt-6 inline-block rounded-md bg-amber-500 px-6 py-3 font-display text-sm font-bold uppercase text-[#07090e] transition-all hover:bg-amber-400"
              >
                Back to sign in
              </Link>
            </div>
          ) : (
            <form onSubmit={submit} className="mt-6 space-y-4" data-testid="reset-form">
              <div className="relative">
                <input
                  data-testid="reset-password-input"
                  type={showPw ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="New password (min 8 characters)"
                  required
                  minLength={8}
                  className={`${inputCls} pr-11`}
                />
                <button
                  data-testid="reset-password-toggle"
                  type="button"
                  onClick={() => setShowPw(!showPw)}
                  aria-label={showPw ? "Hide password" : "Show password"}
                  className="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-500 transition-colors hover:text-amber-500"
                >
                  {showPw ? <EyeOff className="h-4 w-4" strokeWidth={1.75} /> : <Eye className="h-4 w-4" strokeWidth={1.75} />}
                </button>
              </div>
              <div className="relative">
                <input
                  data-testid="reset-confirm-input"
                  type={showConfirm ? "text" : "password"}
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  placeholder="Repeat new password"
                  required
                  minLength={8}
                  className={`${inputCls} pr-11`}
                />
                <button
                  data-testid="reset-confirm-toggle"
                  type="button"
                  onClick={() => setShowConfirm(!showConfirm)}
                  aria-label={showConfirm ? "Hide password" : "Show password"}
                  className="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-500 transition-colors hover:text-amber-500"
                >
                  {showConfirm ? <EyeOff className="h-4 w-4" strokeWidth={1.75} /> : <Eye className="h-4 w-4" strokeWidth={1.75} />}
                </button>
              </div>
              {error && <p data-testid="reset-error" className="text-sm text-red-400">{error}</p>}
              <button
                data-testid="reset-submit-btn"
                type="submit"
                disabled={loading}
                className="flex h-12 w-full items-center justify-center gap-2 rounded-md bg-amber-500 font-display text-base font-bold uppercase text-[#07090e] transition-all hover:bg-amber-400 disabled:opacity-50"
              >
                {loading && <Loader2 className="h-4 w-4 animate-spin" />}
                Set new password
              </button>
              <p className="text-center font-mono-tag text-[9px] text-slate-600">The link works once and expires 1 hour after it was sent.</p>
            </form>
          )}
        </div>
      </main>
    </div>
  );
}
